import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { Brand } from '../../shared/enums'
import { ErrorResponseSchema } from '../../shared/types'
import { netsuiteMappingContract } from './netsuite-mapping/contract'
import {
  BundleChildSchema,
  MealRecommendationInputSchema,
  ProductSchema,
  ProductStatus,
  ProductType,
  ProductVariantSchema,
  RecommendedMealSchema,
} from './schemas'

const c = initContract()

export const productContract = c.router(
  {
    getProducts: {
      method: 'GET',
      path: '/products',
      summary: 'Retrieve a list of products',
      description:
        'Fetch a detailed list of products including information on variants, images, and metadata. Filters by brand (defaults to lyka for backwards compatibility).',
      query: z.object({
        type: z.nativeEnum(ProductType).optional(),
        brand: z.nativeEnum(Brand).optional().default(Brand.LYKA),
        status: z.nativeEnum(ProductStatus).optional(),
      }),
      responses: {
        200: z.object({
          data: z.array(ProductSchema),
        }),
      },
    },
    getProductVariant: {
      method: 'GET',
      path: '/variants/:variantId',
      summary: 'Retrieve a specific product variant',
      description: 'Fetch detailed information for a specific variant by variantId',
      pathParams: z.object({
        variantId: z.string().uuid(),
      }),
      responses: {
        200: z.object({
          data: ProductVariantSchema,
        }),
      },
    },
    getProductVariantBySku: {
      method: 'GET',
      path: '/skus/:sku',
      summary: 'Retrieve a product variant by its SKU',
      description: 'Fetch detailed information for a variant by its globally unique SKU',
      pathParams: z.object({
        sku: z.string().min(1),
      }),
      responses: {
        200: z.object({
          data: ProductVariantSchema,
        }),
      },
    },
    getBundleChildren: {
      method: 'GET',
      path: '/variants/:variantId/bundle-children',
      summary: 'Retrieve bundle child variants for a parent bundle variant',
      description:
        'Returns the child variants and per-bundle quantities for a bundle parent variant. Does not modify the ProductSchema returned by getProducts.',
      pathParams: z.object({
        variantId: z.string().uuid(),
      }),
      responses: {
        200: z.object({
          data: z.array(BundleChildSchema),
        }),
        400: ErrorResponseSchema,
      },
    },
    updateProductVariantStatus: {
      method: 'PATCH',
      path: '/variants/:variantId/status',
      summary: 'Update product variant status',
      description: 'Update the status of a specific product variant by variantId',
      pathParams: z.object({
        variantId: z.string().uuid(),
      }),
      body: z.object({
        status: z.nativeEnum(ProductStatus),
      }),
      responses: {
        200: z.object({
          data: ProductVariantSchema,
        }),
      },
    },
    recommendMeals: {
      method: 'POST',
      path: '/products/recommend-meals',
      summary: 'Recommends meals based on specified conditions',
      description: 'Recommends meals based on specified conditions',
      body: MealRecommendationInputSchema,
      responses: {
        200: z.object({
          data: RecommendedMealSchema.array(),
        }),
      },
    },
  },
  {
    pathPrefix: '/api/v1',
    commonResponses: {
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
)

export const healthContract = c.router({
  getHealth: {
    method: 'GET',
    path: '/health',
    summary: 'Health check endpoint',
    description: 'Checks if the service is healthy',
    responses: {
      200: z.object({
        status: z.string(),
      }),
    },
  },
})

export { netsuiteMappingContract }

export const productServiceContract = {
  ...productContract,
  ...healthContract,
  ...netsuiteMappingContract,
}
